import { DATE_FORMAT } from 'constants/dateFormat';
import dayjs, { Dayjs } from 'utils/dayjs';

export type ParseableDate = Dayjs | Date | string | number | null | undefined;

/**
 * Parse a date value that may be ISO, `DD.MM.YYYY`, `DD.MM.YYYY HH:mm`, etc.
 * Prefer this over bare `dayjs(value)` when the string shape is not guaranteed to be ISO.
 */
export const parseDate = (date: ParseableDate): Dayjs => {
  // customParseFormat is strict: a Date/Dayjs passed with a format array yields Invalid, and each
  // listed format must match exactly. So handle non-strings natively, list every string shape we
  // receive (incl. plain dates), and fall back to native ISO parsing.
  if (typeof date !== 'string') return dayjs(date);

  const parsed = dayjs(date, [
    'YYYY-MM-DDTHH:mm:ssZ',
    'DD-MM-YYYY HH:mm',
    'DD.MM.YYYY HH:mm',
    'YYYY-MM-DD HH:mm',
    'YYYY-MM-DD',
    'DD.MM.YYYY',
  ]);

  return parsed.isValid() ? parsed : dayjs(date);
};

/**
 * Return date with start of the day without timezone
 *
 * 2024-11-11T10:41:10.766Z => 2024-11-11T00:00:00.000Z
 */
export const startOfDay = (date: string | Dayjs | Date) => {
  return dayjs.utc(parseDate(date).startOf('day').format(DATE_FORMAT), DATE_FORMAT);
};

export const isValidDate = (date: ParseableDate): date is Exclude<ParseableDate, null | undefined> => {
  if (date == null) return false;

  const parsed = typeof date === 'string' || typeof date === 'number' ? parseDate(date) : dayjs(date);

  return parsed.isValid();
};

export const isInYearlyPeriod = (date: Dayjs, start: string, end: string): boolean => {
  const year = date.year();
  const startDate = dayjs(`${year}-${start}`, 'YYYY-MM-DD');
  const endDate = dayjs(`${year}-${end}`, 'YYYY-MM-DD');

  if (endDate.isBefore(startDate)) {
    return date.isSameOrAfter(startDate, 'day') || date.isSameOrBefore(endDate, 'day');
  }

  return date.isBetween(startDate, endDate, 'day', '[]');
};

export const isBeforeToday = (date: ParseableDate): boolean => {
  if (!isValidDate(date)) return false;

  const parsed = dayjs.isDayjs(date) ? date.clone().startOf('day') : parseDate(date).startOf('day');

  return parsed.isBefore(dayjs().startOf('day'));
};
